import { useRef, useState } from 'react';

import {
    type FieldPath,
    type FieldValues,
    useEvent,
    useFormContext,
    useFormWatch
} from '@american-autonomy/ag-ui';
import type { FormPathValue } from '@american-autonomy/ag-ui/dist/hooks/useForm/types';

import ClientSelectPanelField from 'components/fields/ClientSelectPanelField';
import FarmSelectPanelField from 'components/fields/FarmSelectPanelField';
import {
    CLIENT_FIELD_NAME,
    FARM_FIELD_NAME
} from 'components/fields/SelectClientFarmField/constants';
import type { ClientFarmSchema, Panel } from 'components/fields/SelectClientFarmField/types';

type UseDataProps<T extends FieldValues = ClientFarmSchema> = {
    namePrefix?: FieldPath<T>;
};

const useData = <T extends FieldValues = ClientFarmSchema>({ namePrefix }: UseDataProps<T>) => {
    const { control, setValue } = useFormContext<T>();

    const fieldName = (name: keyof ClientFarmSchema) =>
        (namePrefix ? `${namePrefix}.${name}` : String(name)) as FieldPath<T>;

    const [openPanel, setOpenPanel] = useState<Panel>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    const clientFormValue = useFormWatch({
        control,
        name: fieldName(CLIENT_FIELD_NAME)
    });

    const farmFormValue = useFormWatch({
        control,
        name: fieldName(FARM_FIELD_NAME)
    });

    const panels = {
        [CLIENT_FIELD_NAME]: {
            component: ClientSelectPanelField,
            value: clientFormValue
        },
        [FARM_FIELD_NAME]: {
            component: FarmSelectPanelField,
            value: farmFormValue
        }
    };

    const hasIncompleteSelection = !!clientFormValue && !farmFormValue;

    const handlePanelOpen = (panel: Panel) => {
        setOpenPanel(panel);
    };

    const handleChange = (panel: Panel, value: { label: string; value: string }) => {
        if (!panel) {
            return;
        }

        setValue(fieldName(panel), value as FormPathValue<T, FieldPath<T>>, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true
        });

        if (panel === CLIENT_FIELD_NAME) {
            setValue(fieldName(FARM_FIELD_NAME), null as FormPathValue<T, FieldPath<T>>, {
                shouldDirty: true,
                shouldTouch: true,
                shouldValidate: true
            });
        }
    };

    const handlePanelClose = (selectedValue?: { label: string; value: string }) => {
        if (!selectedValue || !openPanel) {
            setOpenPanel(null);

            return;
        }

        if (openPanel === CLIENT_FIELD_NAME) {
            setOpenPanel(FARM_FIELD_NAME);
        } else {
            setOpenPanel(null);
        }
    };

    const handleClickOutside = (event: Event) => {
        const target = event.target as HTMLElement;

        if (!containerRef.current?.contains(target) && openPanel) {
            setOpenPanel(null);
        }
    };

    useEvent({ event: 'mouseup', handler: handleClickOutside });

    return {
        clientFormValue,
        containerRef,
        control,
        farmFormValue,
        fieldName,
        handleChange,
        handlePanelClose,
        handlePanelOpen,
        hasIncompleteSelection,
        openPanel,
        panels
    };
};

export default useData;
